import { useState, useMemo } from 'react';
import { useProducts, useDivisions, useProjectMetadata, useUpsertProjectMetadata, useAuditLogs } from '../../hooks';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Spinner } from '../../components/ui/spinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { CheckCircle2, Save, Filter, FilterX, AlertCircle, History, Search, X } from 'lucide-react';
import { formatDateTimeUK } from '../../lib/date-utils';
import { toast } from 'sonner';

interface RowEdit {
  division: string | null;
  softwareCompany: string | null;
}

const NONE_VALUE = '__none__';

const CANONICAL_SOFTWARE_COMPANIES = [
  'HOS - Accommodation',
  'HOS - Food and Beverage',
  'HSC - Care',
  'HSC - Healthcare',
  'HSC - Local Gov & Tech',
  'Legal',
  'NPE',
  'Payments',
  'PPL - Learn',
  'PPL - MLB',
  'PPL - SMB',
  'Recruitment',
  'Central',
  'APAC - Accounting & Tax',
  'APAC - ERP',
  'APAC - People & Payroll',
  'ERP - Small Business',
  'ERP - Core',
  'ERP - Construction',
  'ERP - Specialised',
];

function AuditDialog({ projectName }: { projectName: string }) {
  const { data, isLoading } = useAuditLogs(projectName);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="View audit history">
          <History className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Audit History — {projectName}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner className="h-5 w-5" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground py-4">No audit history found.</p>
        ) : (
          <div className="space-y-3">
            {data.items.map((entry) => (
              <div key={entry.id} className="rounded border p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">{entry.action}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDateTimeUK(entry.changed_at)}</span>
                </div>
                <p className="text-muted-foreground">By: {entry.changed_by}</p>
                {entry.details && <p className="text-xs text-muted-foreground">{entry.details}</p>}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProjectSettingsContent() {
  const { data: products = [], isLoading: productsLoading } = useProducts({ limit: 1000 });
  const { data: divisions = [], isLoading: divisionsLoading } = useDivisions();
  const { data: metadata = [], isLoading: metadataLoading } = useProjectMetadata();
  const upsertMutation = useUpsertProjectMetadata();

  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [savingRow, setSavingRow] = useState<string | null>(null);
  const [divisionFilter, setDivisionFilter] = useState<string>('all');
  const [softwareCompanyFilter, setSoftwareCompanyFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const metadataMap = useMemo(() => {
    const map = new Map<string, typeof metadata[number]>();
    for (const m of metadata) {
      map.set(m.area_level_1, m);
    }
    return map;
  }, [metadata]);

  const uniqueProjects = useMemo(() => {
    const seen = new Set<string>();
    return products.filter((p) => {
      if (!p.project_name || seen.has(p.project_name)) return false;
      seen.add(p.project_name);
      return true;
    });
  }, [products]);

  const unmappedCount = useMemo(() => {
    return uniqueProjects.filter((p) => {
      const meta = metadataMap.get(p.project_name);
      const effectiveDivision = meta?.division ?? p.division;
      return !effectiveDivision;
    }).length;
  }, [uniqueProjects, metadataMap]);

  const filteredProjects = useMemo(() => {
    let result = uniqueProjects;

    if (showUnmappedOnly) {
      result = result.filter((p) => {
        const meta = metadataMap.get(p.project_name);
        const effectiveDivision = meta?.division ?? p.division;
        return !effectiveDivision;
      });
    }

    if (divisionFilter !== 'all') {
      result = result.filter((p) => {
        const meta = metadataMap.get(p.project_name);
        const effectiveDivision = meta?.division ?? p.division;
        return effectiveDivision === divisionFilter;
      });
    }

    if (softwareCompanyFilter !== 'all') {
      result = result.filter((p) => {
        const meta = metadataMap.get(p.project_name);
        const effectiveSC = meta?.software_company ?? p.software_company;
        return effectiveSC === softwareCompanyFilter;
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((p) => p.project_name.toLowerCase().includes(query));
    }

    return result;
  }, [uniqueProjects, metadataMap, showUnmappedOnly, divisionFilter, softwareCompanyFilter, searchQuery]);

  function handleDivisionFilterChange(value: string) {
    setDivisionFilter(value);
    setSoftwareCompanyFilter('all');
  }

  const availableSoftwareCompanies = useMemo(() => {
    if (divisionFilter === 'all') return CANONICAL_SOFTWARE_COMPANIES;
    return CANONICAL_SOFTWARE_COMPANIES;
  }, [divisionFilter]);

  function getRowDivision(projectName: string, product: typeof products[number]): string | null {
    if (edits[projectName]?.division !== undefined) return edits[projectName].division;
    const meta = metadataMap.get(projectName);
    if (meta?.division) return meta.division;
    return product.division || null;
  }

  function getRowSoftwareCompany(projectName: string, product: typeof products[number]): string | null {
    if (edits[projectName]?.softwareCompany !== undefined) return edits[projectName].softwareCompany;
    const meta = metadataMap.get(projectName);
    if (meta?.software_company) return meta.software_company;
    return product.software_company || null;
  }

  function isManuallySet(projectName: string): boolean {
    return metadataMap.has(projectName) && (metadataMap.get(projectName)!.division !== null || metadataMap.get(projectName)!.software_company !== null);
  }

  function handleDivisionChange(projectName: string, value: string, product: typeof products[number]) {
    setEdits((prev) => ({
      ...prev,
      [projectName]: {
        division: value === NONE_VALUE ? null : value,
        softwareCompany: prev[projectName]?.softwareCompany ?? getRowSoftwareCompany(projectName, product),
      },
    }));
  }

  function handleSoftwareCompanyChange(projectName: string, value: string, product: typeof products[number]) {
    setEdits((prev) => ({
      ...prev,
      [projectName]: {
        division: prev[projectName]?.division ?? getRowDivision(projectName, product),
        softwareCompany: value === NONE_VALUE ? null : value,
      },
    }));
  }

  async function handleSave(projectName: string) {
    const edit = edits[projectName];
    if (!edit) return;

    setSavingRow(projectName);
    try {
      await upsertMutation.mutateAsync({
        areaLevel1: projectName,
        body: {
          division: edit.division,
          software_company: edit.softwareCompany,
        },
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[projectName];
        return next;
      });
      toast.success(`Saved metadata for ${projectName}`);
    } catch {
      toast.error(`Failed to save metadata for ${projectName}`);
    } finally {
      setSavingRow(null);
    }
  }

  const isLoading = productsLoading || divisionsLoading || metadataLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner className="h-8 w-8" />
        <span className="ml-3 text-muted-foreground">Loading project data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground mt-1">
          Manage Division and Software Company mappings for each project.
        </p>
      </div>

      {unmappedCount > 0 ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>{unmappedCount}</strong> project{unmappedCount !== 1 ? 's have' : ' has'} no Division set.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription>All projects have a Division set.</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <Button
          variant={showUnmappedOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
        >
          {showUnmappedOnly ? (
            <><FilterX className="h-4 w-4 mr-1" /> Show All</>
          ) : (
            <><Filter className="h-4 w-4 mr-1" /> Unmapped Only</>
          )}
        </Button>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Division</label>
          <div className="flex items-center gap-1">
            <Select value={divisionFilter} onValueChange={handleDivisionFilterChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Divisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {divisions.map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {divisionFilter !== 'all' && (
              <button onClick={() => handleDivisionFilterChange('all')} className="p-1 text-muted-foreground hover:text-foreground" type="button">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Software Company</label>
          <div className="flex items-center gap-1">
            <Select value={softwareCompanyFilter} onValueChange={setSoftwareCompanyFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Software Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Software Companies</SelectItem>
                {availableSoftwareCompanies.map((sc) => (
                  <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {softwareCompanyFilter !== 'all' && (
              <button onClick={() => setSoftwareCompanyFilter('all')} className="p-1 text-muted-foreground hover:text-foreground" type="button">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Search Project</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by project name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-[220px]"
            />
          </div>
        </div>

        <span className="text-sm text-muted-foreground self-end pb-2">
          Showing {filteredProjects.length} of {uniqueProjects.length} projects
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[220px]">Project Name</TableHead>
              <TableHead className="w-[180px]">Division</TableHead>
              <TableHead className="w-[180px]">Software Company</TableHead>
              <TableHead className="w-[160px]">Last Updated</TableHead>
              <TableHead className="w-[160px]">Updated By</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
              <TableHead className="w-[60px]">Audit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredProjects.map((product) => {
              const projectName = product.project_name;
              const meta = metadataMap.get(projectName);
              const hasEdit = projectName in edits;
              const manuallySet = isManuallySet(projectName);
              const currentDivision = getRowDivision(projectName, product);
              const currentSoftwareCompany = getRowSoftwareCompany(projectName, product);
              const divisionNames = divisions.map((d) => d.name);
              const showDatabricksDivision = currentDivision && !divisionNames.includes(currentDivision);
              const showDatabricksSoftwareCompany = currentSoftwareCompany && !CANONICAL_SOFTWARE_COMPANIES.includes(currentSoftwareCompany);

              return (
                <TableRow key={projectName} className={manuallySet ? 'bg-green-50/50' : ''}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {projectName}
                      {manuallySet && (
                        <Badge variant="outline" className="text-green-700 border-green-300 text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Mapped
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={currentDivision ?? NONE_VALUE}
                      onValueChange={(v) => handleDivisionChange(projectName, v, product)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select division" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>
                          <span className="text-muted-foreground">None</span>
                        </SelectItem>
                        {showDatabricksDivision && (
                          <SelectItem value={currentDivision}>
                            <span className="text-muted-foreground">{currentDivision} (from DataBricks)</span>
                          </SelectItem>
                        )}
                        {divisions.map((d) => (
                          <SelectItem key={d.id} value={d.name}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={currentSoftwareCompany ?? NONE_VALUE}
                      onValueChange={(v) => handleSoftwareCompanyChange(projectName, v, product)}
                    >
                      <SelectTrigger className="w-[170px]">
                        <SelectValue placeholder="Select software company" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_VALUE}>
                          <span className="text-muted-foreground">None</span>
                        </SelectItem>
                        {showDatabricksSoftwareCompany && (
                          <SelectItem value={currentSoftwareCompany}>
                            <span className="text-muted-foreground">{currentSoftwareCompany} (from DataBricks)</span>
                          </SelectItem>
                        )}
                        {CANONICAL_SOFTWARE_COMPANIES.map((sc) => (
                          <SelectItem key={sc} value={sc}>
                            {sc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {meta?.updated_at ? formatDateTimeUK(meta.updated_at) : '-'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate max-w-[160px]">
                    {meta?.updated_by ?? '-'}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      disabled={!hasEdit || savingRow === projectName}
                      onClick={() => handleSave(projectName)}
                    >
                      {savingRow === projectName ? (
                        <Spinner className="h-4 w-4" />
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-1" /> Save
                        </>
                      )}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <AuditDialog projectName={projectName} />
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredProjects.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  {showUnmappedOnly ? 'All projects have been mapped!' :
                   searchQuery.trim() ? 'No projects match your search.' :
                   'No projects found.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
